import { CalendarX, PackageSearch } from 'lucide-react';
import { ProductCard } from './ProductCard';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import type { CatalogProduct, Product } from '@/types/product';

interface ProductGridProps {
  products: Product[];
  isLoading: boolean;
  isError: boolean;
  onSelect: (product: Product) => void;
  onRetry?: () => void;
  /** Date-resolved prices keyed by product id. */
  resolvedPrices?: Record<string, number>;
  /** Visit date (YYYY-MM-DD) — drives per-ticket slot capacity + expiry. */
  visitDate: string;
  /** The visit date is operator-blocked — every card is locked. */
  blocked?: boolean;
  /**
   * Every ticket was hidden because none has a bookable slot on the visit date
   * (all ended, full or closed). Says so instead of "not found", which would
   * send the cashier hunting with the search box.
   */
  allHidden?: boolean;
}

export function ProductGrid({
  products,
  isLoading,
  isError,
  onSelect,
  onRetry,
  resolvedPrices,
  visitDate,
  blocked = false,
  allHidden = false,
}: ProductGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 wide:grid-cols-4 hd:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="overflow-hidden rounded-lg border-2 border-border bg-card">
            <Skeleton className="aspect-[4/3] w-full rounded-none" />
            <div className="space-y-2 p-3">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState message="Tidak dapat memuat katalog produk." onRetry={onRetry} />;
  }

  if (products.length === 0 && allHidden) {
    return (
      <EmptyState
        icon={CalendarX}
        title="Tiket untuk tanggal ini sudah habis"
        description="Semua slot sudah penuh, ditutup, atau waktunya sudah lewat. Pilih tanggal kunjungan lain."
      />
    );
  }

  if (products.length === 0) {
    return (
      <EmptyState
        icon={PackageSearch}
        title="Produk tidak ditemukan"
        description="Coba ubah kategori atau kata kunci pencarian."
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 wide:grid-cols-4 hd:grid-cols-5">
      {products.map((p, i) => (
        <ProductCard
          key={p.id}
          product={p}
          index={i}
          onSelect={onSelect}
          resolvedPrice={resolvedPrices?.[p.id]}
          promoPrice={(p as Partial<CatalogProduct>).promoPrice ?? null}
          promos={(p as Partial<CatalogProduct>).promos ?? []}
          visitDate={visitDate}
          blocked={blocked}
        />
      ))}
    </div>
  );
}
